import { api } from '../client';

export interface RiskProfile {
  id: string;
  companyId: string;
  itemId: string;
  item?: {
    id: string;
    name: string;
    sku: string;
    category?: string;
    unitCost?: number;
  };
  supplyRisk: number;
  demandRisk: number;
  qualityRisk: number;
  priceRisk: number;
  obsolescenceRisk: number;
  overallRisk: number;
  singleSourced: boolean;
  longLeadTime: boolean;
  highValue: boolean;
  criticalItem: boolean;
  volatilePricing: boolean;
  mitigationStrategy?: string;
  alternateSuppliers: string[];
  safetyStockDays: number;
  lastAssessedAt: string;
  assessedById?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRiskProfileDto {
  itemId: string;
  supplyRisk?: number;
  demandRisk?: number;
  qualityRisk?: number;
  priceRisk?: number;
  obsolescenceRisk?: number;
  singleSourced?: boolean;
  longLeadTime?: boolean;
  highValue?: boolean;
  criticalItem?: boolean;
  volatilePricing?: boolean;
  mitigationStrategy?: string;
  alternateSuppliers?: string[];
  safetyStockDays?: number;
  notes?: string;
}

export interface UpdateRiskProfileDto {
  supplyRisk?: number;
  demandRisk?: number;
  qualityRisk?: number;
  priceRisk?: number;
  obsolescenceRisk?: number;
  singleSourced?: boolean;
  longLeadTime?: boolean;
  highValue?: boolean;
  criticalItem?: boolean;
  volatilePricing?: boolean;
  mitigationStrategy?: string;
  alternateSuppliers?: string[];
  safetyStockDays?: number;
  notes?: string;
}

export interface QueryRiskProfileParams {
  minRisk?: number;
  maxRisk?: number;
  singleSourced?: boolean;
  criticalItem?: boolean;
  longLeadTime?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface RiskDashboard {
  totalProfiles: number;
  highRiskCount: number;
  criticalItemCount: number;
  singleSourcedCount: number;
  longLeadTimeCount: number;
  riskDistribution: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
  topRiskItems: RiskProfile[];
}

interface RiskPaginatedResponse {
  data: RiskProfile[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const riskManagementApi = {
  // Get all risk profiles with filtering
  getAll: (params?: QueryRiskProfileParams) =>
    api.get<RiskPaginatedResponse>('/risk-profiles', { params }),

  // Get dashboard data
  getDashboard: () => api.get<RiskDashboard>('/risk-profiles/dashboard'),

  // Get high-risk items
  getHighRisk: (threshold?: number) =>
    api.get<RiskProfile[]>('/risk-profiles/high-risk', {
      params: { threshold },
    }),

  // Get single risk profile
  getById: (id: string) => api.get<RiskProfile>(`/risk-profiles/${id}`),

  // Create risk profile
  create: (data: CreateRiskProfileDto) => api.post<RiskProfile>('/risk-profiles', data),

  // Update risk profile
  update: (id: string, data: UpdateRiskProfileDto) =>
    api.patch<RiskProfile>(`/risk-profiles/${id}`, data),

  // Run risk assessment
  runAssessment: (id: string) => api.post<RiskProfile>(`/risk-profiles/${id}/assess`),

  // Delete risk profile
  delete: (id: string) => api.delete(`/risk-profiles/${id}`),
};
